/**
 * Application shell: sidebar (spec §118), topbar, filter bar, outlet.
 *
 * Three layout decisions, all forced by the fact that this gets opened on a
 * phone in a hospital corridor as often as on a desk:
 *
 * 1. **The main column scrolls, not the page.** The sidebar, freshness bar and
 *    filter bar stay put while a 300-row table moves under them. Scrolling the
 *    whole document instead pushes the period filter off-screen exactly when
 *    someone is reading the numbers it controls.
 * 2. **The sidebar is a drawer below `lg`.** A 212px rail on a 390px screen
 *    leaves 178px for the content, which is not a layout.
 * 3. **It is collapsible on desktop too.** Wide tables — the comparison screen
 *    has eleven columns — are the main thing people came for, and 212px back is
 *    a whole column.
 *
 * The collapsed/expanded choice persists, because re-collapsing the sidebar on
 * every navigation is the kind of small rudeness that makes a tool feel cheap.
 */

import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, ArrowLeft, Award, BarChart3, Brain, Building2,
  ChevronLeft, Compass, Database, GaugeCircle, LayoutDashboard, ListChecks,
  LogOut, Menu, PanelLeft, Target, TrendingUp, UserCheck, UserCog, Users, X,
} from "lucide-react";

import { FilterBar } from "@/components/filters/FilterBar";
import { FreshnessBar } from "@/components/cards/FreshnessBar";
import { useAuthStore } from "@/store/auth";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Only the index route needs exact matching. */
  end?: boolean;
  adminOnly?: boolean;
}

// Typed explicitly: inferred from the literal, `end` widens to `unknown` as
// soon as one group's items omit it, and `end={item.end}` stops compiling.
const NAV: Array<{ section: string; items: NavItem[] }> = [
  {
    section: "Command Center",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    section: "Intelligence",
    items: [
      { to: "/opportunities", label: "Opportunities", icon: Compass },
      { to: "/risks", label: "Risks", icon: AlertTriangle },
    ],
  },
  {
    section: "Analytics",
    items: [
      { to: "/funnel", label: "Conversion funnel", icon: BarChart3 },
      { to: "/vps", label: "Team performance", icon: Building2 },
      { to: "/performance", label: "Employee comparison", icon: TrendingUp },
      { to: "/behaviour", label: "Behavioural analytics", icon: Brain },
      { to: "/activities", label: "Activities", icon: ListChecks },
    ],
  },
  {
    section: "Explore",
    items: [
      { to: "/clients", label: "Clients", icon: Users },
      { to: "/employees", label: "Employees", icon: Activity },
      { to: "/employee-kpis", label: "Employee KPIs", icon: Award },
    ],
  },
  {
    section: "Administration",
    items: [
      { to: "/targets", label: "Targets", icon: Target },
      { to: "/quality", label: "Data quality", icon: Database },
      { to: "/health", label: "System health", icon: GaugeCircle },
      // Hidden from everyone else. The API enforces this too — a link nobody
      // else can see is a courtesy, not a control.
      { to: "/users", label: "Users", icon: UserCog, adminOnly: true },
      { to: "/access-requests", label: "Access requests", icon: UserCheck,
        adminOnly: true },
    ],
  },
];

const COLLAPSE_KEY = "mcc-sidebar-collapsed";

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isAdmin = user?.role === "ADMIN";
  const location = useLocation();
  const navigate = useNavigate();
  // The dashboard is the root: there is nowhere to go back *to* from it, and a
  // dead control is worse than no control.
  const canGoBack = location.pathname !== "/";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === "1");

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Navigating on a phone must close the drawer, or the destination is hidden
  // behind the menu that was used to reach it.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The drawer overlays the page, so the page behind it must not scroll.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const railWidth = collapsed ? "lg:w-[60px]" : "lg:w-[212px]";

  return (
    // h-screen + overflow-hidden: the shell owns the viewport and the main
    // column does the scrolling, so the filter bar never scrolls away.
    <div className="h-screen overflow-hidden flex bg-canvas">
      {/* ---- scrim, phone only ---- */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 bg-ink/40 lg:hidden"
             onClick={() => setDrawerOpen(false)} role="presentation" />
      )}

      {/* ---- sidebar: drawer under lg, rail from lg ---- */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[248px] bg-surface border-r border-rule
                    flex flex-col transition-transform duration-200
                    lg:static lg:z-auto lg:translate-x-0 lg:transition-[width]
                    ${railWidth}
                    ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center gap-2 px-3 lg:px-4 py-4 border-b border-rule">
          {!collapsed && (
            <div className="font-display font-bold text-[15px] leading-tight
                            tracking-tight flex-1 min-w-0">
              Marketing<br />Command Center
            </div>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden lg:grid place-items-center w-7 h-7 rounded
                       hover:bg-surface-alt text-ink-muted shrink-0"
          >
            {collapsed ? <PanelLeft size={15} /> : <ChevronLeft size={15} />}
          </button>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="lg:hidden grid place-items-center w-8 h-8 rounded
                       hover:bg-surface-alt text-ink-muted shrink-0"
          >
            <X size={17} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain py-3">
          {NAV.map((group) => {
            const items = group.items.filter(
              (item) => !item.adminOnly || isAdmin);
            if (items.length === 0) return null;
            return (
              <div key={group.section} className="mb-4">
                {!collapsed && (
                  <div className="label px-4 mb-1.5">{group.section}</div>
                )}
                {collapsed && <div className="mx-3 mb-2 border-t border-rule" />}
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end ?? false}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 py-2 lg:py-1.5 text-[14px] lg:text-[13.5px]
                       border-l-2 ${collapsed ? "px-0 justify-center" : "px-4"} ${
                        isActive
                          ? "border-petrol text-petrol bg-petrol-soft/60 font-medium"
                          : "border-transparent text-ink-soft hover:bg-surface-alt"
                      }`
                    }
                  >
                    <item.icon size={16} className="shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        {user && (
          <div className={`border-t border-rule ${collapsed ? "px-2 py-3" : "px-4 py-3"}`}>
            {collapsed ? (
              <button onClick={logout} title={`Sign out (${user.display_name})`}
                      aria-label="Sign out"
                      className="w-full grid place-items-center py-1.5 rounded
                                 hover:bg-surface-alt text-ink-muted">
                <LogOut size={15} />
              </button>
            ) : (
              <>
                <div className="text-[13px] font-medium truncate">
                  {user.display_name}
                </div>
                <div className="label mt-0.5">{user.role}</div>
                <button className="btn text-xs mt-2 w-full justify-center"
                        onClick={logout}>
                  <LogOut size={12} /> Sign out
                </button>
              </>
            )}
          </div>
        )}
      </aside>

      {/* ---- main column: the only thing that scrolls ---- */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Phone topbar. The hamburger has a 44px hit area — a 15px icon is
            not a touch target. */}
        <div className="lg:hidden flex items-center gap-2 px-2 py-2 bg-surface
                        border-b border-rule shrink-0">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            className="grid place-items-center w-11 h-11 rounded-md
                       hover:bg-surface-alt text-ink"
          >
            <Menu size={20} />
          </button>
          <div className="font-display font-bold text-[14px] tracking-tight truncate">
            Marketing Command Center
          </div>
        </div>

        <FreshnessBar />
        <FilterBar />

        <div className="flex-1 overflow-y-auto overscroll-contain
                        p-3 sm:p-4 lg:p-5">
          {canGoBack && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 mb-3 -ml-1 px-2 py-1.5
                         rounded-md text-[12.5px] text-ink-muted
                         hover:text-ink hover:bg-surface-alt
                         focus-visible:outline focus-visible:outline-2
                         focus-visible:outline-petrol"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
